// The "pick-several-things-across-categories" pattern. Three columns:
// Data / Insight / Activation. Each column tinted with its layer accent.
// Arrows between columns signal direction. See README.md §3.3.

import type { ReactNode } from "react";
import { LAYER_ACCENTS } from "@/lib/layers";
import type { LayerGroup } from "@/lib/types";

export interface LayerGroupItem<T = unknown> {
  id: string;
  data: T;
}

export interface LayerColumn<T = unknown> {
  id: string;
  layer: LayerGroup;
  layerLabel?: string;
  title: string;
  blurb?: string;
  items: LayerGroupItem<T>[];
}

export interface GroupedLayersProps<T = unknown> {
  groups: LayerColumn<T>[];
  renderItem: (
    item: LayerGroupItem<T>,
    accent: (typeof LAYER_ACCENTS)[LayerGroup],
    group: LayerColumn<T>,
  ) => ReactNode;
  columns?: number;
}

export function GroupedLayers<T>({
  groups,
  renderItem,
  columns = 3,
}: GroupedLayersProps<T>): JSX.Element {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: 12,
      }}
    >
      {groups.map((g, idx) => {
        const acc = LAYER_ACCENTS[g.layer];
        return (
          <div
            key={g.id || idx}
            style={{
              padding: 16,
              borderRadius: 12,
              background: acc.bg,
              border: `1px solid ${acc.border}`,
              position: "relative",
            }}
          >
            <div style={{ marginBottom: 12 }}>
              {g.layerLabel && (
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: ".08em",
                    textTransform: "uppercase",
                    color: acc.fg,
                  }}
                >
                  {g.layerLabel}
                </div>
              )}
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#1a2a3d",
                  marginTop: 2,
                }}
              >
                {g.title}
              </div>
              {g.blurb && (
                <div
                  style={{ fontSize: 11, color: "#4a6785", marginTop: 2 }}
                >
                  {g.blurb}
                </div>
              )}
            </div>
            <div style={{ display: "grid", gap: 6 }}>
              {g.items.map((item) => (
                <div key={item.id}>{renderItem(item, acc, g)}</div>
              ))}
            </div>
            {idx < groups.length - 1 && columns > 1 && (
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  right: -8,
                  top: 54,
                  color: "#aacbe2",
                  fontSize: 18,
                  zIndex: 1,
                }}
              >
                →
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
